import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, InputLabel, TextField, useTheme } from '@mui/material';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { hoekMaat } from "../../lib/hoek";

const TextAreaComponent = ({ label = '', name, style = {}, placeText = '', setField, required, isFeedback = false, error, showLabel, sx = {}, Css }) => {
  const dispatch = useDispatch();
  const { addFields } = useSelector((store) => store?.feedback);

  const theme = useTheme();
  const [value, setValue] = useState('');

  // Debounced Redux dispatch
  const debouncedDispatch = useCallback(
    debounce((updatedFields) => {
      dispatch(setField(updatedFields));
    }, 300),
    [dispatch, setField]
  );

  // Debounced setField for non-feedback logic
  const debouncedSetField = useCallback(
    debounce((name, newValue) => {
      setField(name, newValue);
    }, 300),
    [setField]
  );

  useEffect(() => {
    const existingField = addFields.find((field) => field.name === name);
    if (existingField?.data) {
      setValue(existingField?.data);
    }
  }, [addFields, name]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);

    if (isFeedback) {
      const updatedFields = addFields.some((field) => field.name === name)
        ? addFields.map((field) =>
            field.name === name ? { ...field, data: newValue } : field
          )
        : [...addFields, { name, data: newValue }];

      // Use debounced dispatch
      debouncedDispatch(updatedFields);
    } else {
      // Use debounced setField
      debouncedSetField(name, newValue);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      <TextField
        name={name}
        variant="outlined"
        value={value}
        fullWidth
        multiline
        placeholder={isFeedback ? placeText : label}
        onChange={handleChange}
        sx={{
          flex: 1,
          "& .MuiOutlinedInput-root": {
            height: "100%",
            borderRadius: isFeedback ? hoekMaat(14) : 2,
            backgroundColor: Css?.QuestionCardBgClr || "#FDFDFD",
            border: isFeedback
              ? `1px solid ${Css?.QuestionCardBorderClr || "rgba(0,0,0,0.08)"}`
              : undefined,
            "& fieldset": {
              borderColor: error ? "red" : (isFeedback ? "transparent" : "#EBEBEB"),
            },
            "&:hover fieldset": {
              borderColor: error ? "red" : (isFeedback ? "transparent" : "#EBEBEB"),
            },
            "&.Mui-focused fieldset": {
              borderColor: error ? "red" : "#0B5753",
            },
            "&:hover": isFeedback
              ? { boxShadow: "0 4px 18px rgba(0,0,0,0.07)" }
              : undefined,
            transition: "box-shadow 0.2s",
          },
          "& .MuiInputBase-root": {
            height: "100%",
            alignItems: "flex-start",
          },
          "& textarea": {
            height: "100% !important",
            overflow: "auto !important",
          },
        }}
      />
    </Box>
  );
};

export default TextAreaComponent;
